import { FeaturesReady } from '@growthbook/growthbook-react'
import { lazy, Suspense } from 'react'

import { useAuthenticatedUser } from '@/legacy-src/hooks/useAuthenticatedUser'
import { getTranslate } from '@/legacy-src/utilities/i18n'

import { ErrorBoundary } from '@/src/components/ErrorBoundary'

import { Dialog, DialogContent } from '@/src/domains/design-system/components/Dialog'
import { AiPassphraseRecoveryProvider } from '@/src/domains/ai/components/AiPassphraseRecoveryProvider'
import type { AiEncryptionKey } from '@/src/domains/ai/types'
import { UserType } from '@/src/domains/auth/schemas'

import { AiSetupProviderDialogLoader } from './AiSetupProviderDialogLoader'

export interface AiSetupProviderDialogProps {
  onEncryptionKeySubmit: (encryptionKey: AiEncryptionKey, recoveryKey?: string) => void
  onRecoveryKeySubmit: () => void
  onOpenChange: (isOpen: boolean) => void
}

const AiSetupProviderDialogClient = lazy(() =>
  import('./AiSetupProviderDialogClient').then((module) => ({
    default: module.AiSetupProviderDialogClient,
  })),
)

const AiSetupProviderDialogTherapist = lazy(() =>
  import('./AiSetupProviderDialogTherapist').then((module) => ({
    default: module.AiSetupProviderDialogTherapist,
  })),
)

const dialogVariantByUserType = {
  [UserType.CLIENT]: AiSetupProviderDialogClient,
  [UserType.THERAPIST]: AiSetupProviderDialogTherapist,
}

export function AiSetupProviderDialog({
  onEncryptionKeySubmit,
  onRecoveryKeySubmit,
  onOpenChange,
}: AiSetupProviderDialogProps) {
  const { t } = getTranslate()
  const { user } = useAuthenticatedUser()

  const DialogVariant = dialogVariantByUserType[user.type as UserType]

  return (
    <Dialog open onOpenChange={onOpenChange}>
      {/* aria-label names the dialog when the error fallback replaces the step and its DialogTitle. */}
      <DialogContent desktopMaxWidth={480} aria-label={t('component.ai.passphrase.dialogTitle')}>
        <ErrorBoundary>
          <Suspense fallback={<AiSetupProviderDialogLoader />}>
            <FeaturesReady fallback={<AiSetupProviderDialogLoader />}>
              <AiPassphraseRecoveryProvider>
                <DialogVariant
                  onEncryptionKeySubmit={onEncryptionKeySubmit}
                  onRecoveryKeySubmit={onRecoveryKeySubmit}
                  onClose={() => onOpenChange(false)}
                />
              </AiPassphraseRecoveryProvider>
            </FeaturesReady>
          </Suspense>
        </ErrorBoundary>
      </DialogContent>
    </Dialog>
  )
}
